import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { ReportHeader } from '../../components/ReportHeader';
import { ExportButton } from '../../components/ExportButton';

const API = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FILTERS = { domain: '', partner_name: '', source: '', from_date: '', to_date: '', search: '' };

function Pagination({ page, totalPages, total, pageSize, onPage }) {
  if (totalPages <= 1) return null;
  const start = Math.max(1, page - 2);
  const end   = Math.min(totalPages, page + 2);
  const pages = [];
  for (let i = start; i <= end; i++) pages.push(i);
  return (
    <div className="pagination" data-testid="activity-pagination">
      <span className="pagination-info">
        {((page - 1) * pageSize + 1).toLocaleString()}–{Math.min(page * pageSize, total).toLocaleString()} of {total.toLocaleString()}
      </span>
      <button className="page-btn" disabled={page <= 1} onClick={() => onPage(1)}>«</button>
      <button className="page-btn" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="activity-prev-btn">‹</button>
      {pages.map(p => (
        <button key={p} className={`page-btn${p === page ? ' active' : ''}`} onClick={() => onPage(p)}>{p}</button>
      ))}
      <button className="page-btn" disabled={page >= totalPages} onClick={() => onPage(page + 1)} data-testid="activity-next-btn">›</button>
      <button className="page-btn" disabled={page >= totalPages} onClick={() => onPage(totalPages)}>»</button>
    </div>
  );
}

export default function LearnerActivity() {
  const [stats, setStats] = useState({ total_registered_learners: 0, total_enrollments: 0, total_completions: 0 });
  const [periodLabel, setPeriodLabel] = useState('All Time');
  const [filterOpts, setFilterOpts] = useState({});
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [piiAllowed, setPiiAllowed] = useState(false); // fail-closed default
  // Monotonic request ids — guard against out-of-order responses overwriting
  // the latest period selection (stale All-Time/prev-FY response winning).
  const statsReqId = useRef(0);
  const dataReqId = useRef(0);
  const fetchStats = useCallback((f = {}) => {
    const q = {};
    if (f.from_date) q.from_date = f.from_date;
    if (f.to_date)   q.to_date   = f.to_date;
    const myId = ++statsReqId.current;
    axios.get(`${API}/api/reports/learner-activity/stats`, { params: q, withCredentials: true })
      .then(r => { if (myId === statsReqId.current) setStats(r.data); }).catch(console.error);
  }, []);

  useEffect(() => {
    fetchStats();
    axios.get(`${API}/api/reports/filters`, { withCredentials: true })
      .then(r => setFilterOpts(r.data)).catch(console.error);
    fetchData(1, EMPTY_FILTERS);
    // Read pii_allowed from session
    axios.get(`${API}/api/auth/me`, { withCredentials: true })
      .then(r => setPiiAllowed(r.data?.pii_allowed === true))
      .catch(() => setPiiAllowed(false));
  }, []); // eslint-disable-line

  const buildParams = (f, p) => {
    const q = { page: p, page_size: 50 };
    if (f.domain)        q.domain        = f.domain;
    if (f.partner_name)  q.partner_name  = f.partner_name;
    if (f.source)        q.source        = f.source;
    if (f.from_date)     q.from_date     = f.from_date;
    if (f.to_date)       q.to_date       = f.to_date;
    if (f.search)        q.search        = f.search;
    return q;
  };

  const fetchData = useCallback(async (p, f) => {
    const myId = ++dataReqId.current;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/reports/learner-activity/list`,
        { params: buildParams(f, p), withCredentials: true });
      if (myId !== dataReqId.current) return; // a newer request superseded this one
      setData(r.data.data);
      setTotal(r.data.total);
      setTotalPages(r.data.total_pages);
      setPage(p);
    } catch (e) { console.error(e); }
    finally { if (myId === dataReqId.current) setLoading(false); }
  }, []); // eslint-disable-line

  const handlePeriodChange = useCallback(({ fyLabel, startDate, endDate }) => {
    setPeriodLabel(fyLabel || 'All Time');
    const f = { ...EMPTY_FILTERS, from_date: startDate || '', to_date: endDate || '' };
    setFilters(f);
    setApplied(f);
    fetchStats(f);
    fetchData(1, f);
  }, [fetchStats]); // eslint-disable-line

  const handleApply = () => { setApplied(filters); fetchStats(filters); fetchData(1, filters); };
  const handleReset = () => { setFilters(EMPTY_FILTERS); setApplied(EMPTY_FILTERS); fetchStats(); fetchData(1, EMPTY_FILTERS); };
  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  const fmt = d => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return d; }
  };

  return (
    <div data-testid="learner-activity-page">
      <ReportHeader
        reportTitle="Learner Activity Report"
        primaryTable="registered_users"
        onChange={handlePeriodChange}
      />
      <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
        Dashboard &gt; Reports &gt; Learner Activity Report
      </div>
      <div className="page-header">
        <h1>Learner Activity Report</h1>
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3,1fr)' }}>
        <div className="stat-card" data-testid="la-stat-learners">
          <div className="stat-value green">{stats.total_registered_learners.toLocaleString()}</div>
          <div className="stat-label">Total Registered Learners</div>
          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>{periodLabel}</div>
        </div>
        <div className="stat-card" data-testid="la-stat-enrollments">
          <div className="stat-value green">{stats.total_enrollments.toLocaleString()}</div>
          <div className="stat-label">Total Enrollments</div>
          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>{periodLabel}</div>
          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 1, fontStyle: 'italic' }}>all learners</div>
        </div>
        <div className="stat-card" data-testid="la-stat-completions">
          <div className="stat-value green">{stats.total_completions.toLocaleString()}</div>
          <div className="stat-label">Total Completions</div>
          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>{periodLabel}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="filter-bar">
        <div className="filter-group">
          <label className="filter-label">Domain Type</label>
          <select className="filter-select" value={filters.domain}
            onChange={e => setF('domain', e.target.value)} data-testid="la-filter-domain">
            <option value="">All</option>
            {(filterOpts.domains || []).map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label className="filter-label">Partner</label>
          <select className="filter-select" value={filters.partner_name}
            onChange={e => setF('partner_name', e.target.value)} data-testid="la-filter-partner">
            <option value="">All</option>
            {(filterOpts.partners || []).map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label className="filter-label">Source</label>
          <select className="filter-select" value={filters.source}
            onChange={e => setF('source', e.target.value)} data-testid="la-filter-source">
            <option value="">All</option>
            {(filterOpts.sources || []).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <button className="btn btn-primary" onClick={handleApply} data-testid="activity-apply-btn">Apply</button>
          <button className="btn btn-secondary" onClick={handleReset} data-testid="activity-reset-btn">Reset</button>
        </div>
      </div>

      {/* Table */}
      <div className="table-container">
        <div className="table-header">
          <div>
            <span className="table-title">Learner Enrollments</span>
            <span className="table-count" style={{ marginLeft: 8 }}>({total.toLocaleString()} learners)</span>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="text" className="search-input" placeholder="Search learner..."
              value={filters.search} onChange={e => setF('search', e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleApply()}
              data-testid="la-search-input" />
            <ExportButton
              requestUrl="/api/reports/learner-activity/export/request"
              params={applied}
              filename="learner_activity_report.csv"
              testId="la-export-btn"
            />
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
        ) : data.length === 0 ? (
          <div className="empty-state"><h3>No learners found</h3><p>Try adjusting your filters</p></div>
        ) : (
          <>
            <div className="table-scroll">
              <table data-testid="activity-table">
                <thead>
                  <tr>
                    <th>Learner LMS ID</th>
                    {piiAllowed && <th>Learner Name</th>}
                    <th>Country</th>
                    <th>Region</th>
                    <th>Partner Name</th>
                    <th>Registration Status</th>
                    <th>Participation Status</th>
                    <th>Source</th>
                    <th>Last Login Date</th>
                    <th>Joined Date</th>
                    <th className="td-right">Enrollments</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, i) => (
                    <tr key={i} data-testid={`activity-row-${i}`}>
                      <td>{row.learner_lms_id || '—'}</td>
                      {piiAllowed && (
                        <td>{(row.learner_name || '').trim() || '—'}</td>
                      )}
                      <td>{row.country || '—'}</td>
                      <td>{row.region || 'N/A'}</td>
                      <td title={row.partner_name}>{row.partner_name || '—'}</td>
                      <td>
                        <span className={`badge ${row.registration_status === 'Active' ? 'badge-green' : 'badge-gray'}`}>
                          {row.registration_status || '—'}
                        </span>
                      </td>
                      <td>
                        <span className="badge badge-blue">{row.participation_status || '—'}</span>
                      </td>
                      <td>{row.source || '—'}</td>
                      <td>{fmt(row.last_login_date)}</td>
                      <td>{row.joined_at ? row.joined_at.split('T')[0] : '—'}</td>
                      <td className="td-right" style={{ fontWeight: 600 }}>
                        {(row.enrollments || 0).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={totalPages} total={total} pageSize={50}
              onPage={p => fetchData(p, applied)} />
          </>
        )}
      </div>
    </div>
  );
}
